import { useEffect, useState } from 'react';
import { fdCalculatorFields } from '@components/Calculators/fd/helper';
import { calculateFDMaturity, getFDGain } from '@components/Calculators/helpers/utils';

const useFdCalculator = ({ totalInvestment, rate, time }) => {
    const [fields, setFields] = useState({
        totalInvestment: {
            ...fdCalculatorFields.totalInvestment,
            value: totalInvestment,
        },
        expectedRateOfReturn: {
            ...fdCalculatorFields.expectedRateOfReturn,
            value: rate,
        },
        timePeriod: { ...fdCalculatorFields.timePeriod, value: time },
    });
    const [calculatedValue, setCalculatedValues] = useState({
        totalInvested: totalInvestment,
        estimatedReturns: 0,
        totalAmount: 0,
    });

    const calculateFdReturns = async ({ totalInvestment, rate, time }) => {
        const compoundingFrequency = 4; // Assuming interest is compounded quarterly
        const maturityAmount = calculateFDMaturity({
            totalInvestment,
            rate,
            time,
            compoundingFrequency,
        });
        const gain = getFDGain(maturityAmount, totalInvestment);
    
        if (maturityAmount && gain) {
            setCalculatedValues((prev) => ({
                ...prev,
                totalInvested: totalInvestment,
                estimatedReturns: Math.round(gain),
                totalAmount: Math.round(maturityAmount),
            }));
        }
    };

    useEffect(() => {
        const hasError = Object.values(fields).some((field) => field.error);
        if (!hasError) {
            calculateFdReturns({
                totalInvestment: fields.totalInvestment?.value,
                rate: fields.expectedRateOfReturn?.value,
                time: fields.timePeriod?.value,
            });
        } else {
            setCalculatedValues((prev) => ({
                ...prev,
                totalInvested: 0,
                estimatedReturns: 0,
                totalAmount: 0,
            }));
        }
    }, [
        fields.totalInvestment?.value,
        fields.expectedRateOfReturn?.value,
        fields.timePeriod?.value,
    ]);

    const updateFieldState = (id, value, error = null) => {
        setFields((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                value,
                error,
            },
        }));
    };

    const validateValue = (id, value, showError = false) => {
        const { minValue, maxValue } = fields[id];

        if (value < minValue) {
            updateFieldState(
                id,
                value,
                showError ? `Minimum value allowed is ${minValue}` : null
            );
            return;
        }

        if (value > maxValue) {
            updateFieldState(id, maxValue, null);
            return;
        }

        updateFieldState(id, value, null);
    };

    const onChangeCalculatorInput = (id, value) => {
        validateValue(id, value, false);
    };

    const onBlur = (id, value) => {
        validateValue(id, value, true);
    };

    const states = { calculatedValue, fields };
    const callbacks = {
        onChangeCalculatorInput,
        onBlur,
    };
    return { states, callbacks };
};

export default useFdCalculator;
